/* eslint-disable react-hooks/exhaustive-deps */

import {
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import React, { useEffect, useState } from "react";

import LayoutPage from "layouts/LayoutPage";
import background from "assets/replicas/harry-potter/hp-marauders-map/v7/background_global.jpg";
import { decodeQuote } from "utils/functions/secure-link";
import { paypal } from "config";
import styled from "styled-components";

interface QuoteData {
  datetime_created: string;
  total: number;
  items: Array<{
    name: string;
    price: number;
  }>;
}

const Wrapper = styled.div`
  width: 100%;
  max-width: 800px;
  padding-top: min(50px, 5vh);
  padding-bottom: 20px;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;

  background: ${(props) => props.theme.colors.white}DD;
  backdrop-filter: blur(10px);
  border-radius: 8px;

  color: ${(props) => props.theme.colors.black};
`;

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${(props) => props.theme.colors.black}AA;
  text-align: center;
  margin: auto;
  margin-bottom: 8px;
`;

const SelectedReplicasList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SelectedReplicaItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.black}AA;

  ${(props) => props.theme.breakpoints.M} {
    font-size: 12px;
  }
`;

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: min(40px, 5vw);
  border-radius: 4px;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;

  & {
    font-size: 20px;
    font-weight: 700;
    color: #3b7bbf;
  }

  margin: 10px 0;
  padding: 20px 0;

  border: 1px solid ${(props) => props.theme.colors.black}22;
  border-left: none;
  border-right: none;
`;

const ErrorMessage = styled.div`
  margin-top: 20vh;
  color: ${(props) => props.theme.colors.red};
  font-size: 14px;
  font-weight: 600;
  padding: 16px;
  background: ${(props) => props.theme.colors.red}11;
  border-radius: 4px;
  text-align: center;
`;

const LoadingMessage = styled.div`
  margin-top: 20vh;
  color: ${(props) => props.theme.colors.white}88;
  font-size: 14px;
  padding: 16px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  margin-top: 20vh;
  color: ${(props) => props.theme.colors.green};
  font-size: 14px;
  font-weight: 600;
  padding: 16px;
  background: ${(props) => props.theme.colors.green}11;
  border-radius: 4px;
  text-align: center;
`;

const PayPalButtonContainer = styled.div``;

const PayPalErrorMessage = styled.div`
  color: ${(props) => props.theme.colors.red};
  font-size: 12px;
  font-weight: 600;
  padding: 8px 12px;
  background: ${(props) => props.theme.colors.red}11;
  border-radius: 3px;
  text-align: center;
  margin-bottom: 12px;
`;

const Disclaimer = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.black};
  padding: 12px 16px;
  text-align: center;
  margin-top: 8px;
`;

const ReplicasPaymentContent = () => {
  const [quoteData, setQuoteData] = useState<QuoteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [{ isPending }] = usePayPalScriptReducer();
  const expiryDays = parseInt(
    process.env.REACT_APP_REPLICAS_PAYMENT_EXPIRY_DAYS || "4",
    10,
  );

  const isQuoteExpired = (createdDate: Date): boolean => {
    const now = new Date();
    const daysDiff =
      (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff > expiryDays;
  };

  const getRemainingDays = (createdDate: Date): number => {
    const now = new Date();
    const daysPassed =
      (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24);
    const remaining = Math.ceil(expiryDays - daysPassed);
    return Math.max(remaining, 0);
  };

  useEffect(() => {
    const loadQuote = async () => {
      try {
        const queryString = window.location.search.substring(1);
        const decoded = await decodeQuote<QuoteData>(queryString);

        if (!decoded) {
          setError(
            "Invalid or tampered quote data. Please request a new payment link.",
          );
          setLoading(false);
          return;
        }

        // Check if link is older than configured expiry days
        const createdDate = new Date(decoded.datetime_created);
        if (isQuoteExpired(createdDate)) {
          setError(
            `The payment link has expired. Links are valid for ${expiryDays} days.`,
          );
          setLoading(false);
          return;
        }

        setQuoteData(decoded);
        setLoading(false);
      } catch (err) {
        console.error("Error decoding quote:", err);
        setError("Failed to decode quote data");
        setLoading(false);
      }
    };

    loadQuote();
  }, []);

  const createPayPalButton = () => {
    if (!quoteData) return null;

    const items = quoteData.items.map((item) => ({
      name: item.name,
      quantity: "1",
      unit_amount: {
        currency_code: paypal.currency,
        value: `${item.price.toFixed(2)}`,
      },
    }));

    const createOrder = (data, actions) => {
      // Re-validate that quote is still not expired before creating order
      if (!quoteData) {
        throw new Error("Quote data not found");
      }

      const createdDate = new Date(quoteData.datetime_created);
      if (isQuoteExpired(createdDate)) {
        setPaymentError(
          `Payment link has expired. Links are valid for ${expiryDays} days. Please request a new payment link.`,
        );
        throw new Error("Quote has expired");
      }

      return actions.order.create({
        application_context: {
          brand_name: "Jan Hamernik",
          shipping_preference: "NO_SHIPPING",
        },
        purchase_units: [
          {
            description: "Prop Replicas Purchase",
            amount: {
              currency_code: paypal.currency,
              value: `${quoteData.total.toFixed(2)}`,
              breakdown: {
                item_total: {
                  currency_code: paypal.currency,
                  value: `${quoteData.total.toFixed(2)}`,
                },
              },
            },
            items: items,
          },
        ],
      });
    };

    const onApprove = async (data, actions) => {
      return actions.order.capture().then(() => {
        setPaymentSuccess(true);
        setPaymentError(null);
      });
    };

    const onCancel = () => {
      setPaymentError("Payment was cancelled. Please try again.");
    };

    const onError = (error) => {
      console.error("PayPal error:", error);
      setPaymentError(
        "There was a problem processing your payment. Please try again.",
      );
    };

    return (
      <PayPalButtons
        createOrder={createOrder}
        onApprove={onApprove}
        onCancel={onCancel}
        onError={onError}
      />
    );
  };

  return (
    <LayoutPage background={background}>
      {loading ? (
        <LoadingMessage>loading payment...</LoadingMessage>
      ) : error || !quoteData ? (
        <ErrorMessage>{error || "No quote data found"}</ErrorMessage>
      ) : paymentSuccess ? (
        <SuccessMessage>
          Payment successful! Thank you for your purchase.
        </SuccessMessage>
      ) : (
        <Wrapper>
          <SectionTitle>Order Summary</SectionTitle>

          <Summary>
            {quoteData.items.length > 0 && (
              <SelectedReplicasList>
                {quoteData.items.map((item, idx) => (
                  <SelectedReplicaItem key={idx}>
                    <span>{item.name}</span>
                    <span>${item.price}</span>
                  </SelectedReplicaItem>
                ))}
              </SelectedReplicasList>
            )}

            <TotalRow>
              <span>Total:</span>
              <span>${quoteData.total}</span>
            </TotalRow>

            {paymentError && (
              <PayPalErrorMessage>{paymentError}</PayPalErrorMessage>
            )}

            <PayPalButtonContainer>
              {isPending ? <div className="spinner" /> : createPayPalButton()}
            </PayPalButtonContainer>

            <Disclaimer>
              Payment link expires in{" "}
              {getRemainingDays(new Date(quoteData.datetime_created))} day
              {getRemainingDays(new Date(quoteData.datetime_created)) !== 1
                ? "s"
                : ""}
            </Disclaimer>
          </Summary>
        </Wrapper>
      )}
    </LayoutPage>
  );
};

const ReplicasPayment = () => {
  const paypalOptions = {
    "client-id": paypal.sandbox
      ? paypal.clientId.sandbox
      : paypal.clientId.production,
    currency: "USD",
    intent: "capture",
  };

  return (
    <PayPalScriptProvider options={paypalOptions}>
      <ReplicasPaymentContent />
    </PayPalScriptProvider>
  );
};

export default ReplicasPayment;
